import { useState } from 'react';
import { MessageCircle, X, Send } from 'lucide-react';

const WHATSAPP_NUMBER = '971585806264';
const WHATSAPP_MESSAGE = 'Hello! I would like to know more about TD Capital trading services.';

export const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);

  const handleWhatsAppClick = () => {
    const encodedMessage = encodeURIComponent(WHATSAPP_MESSAGE);
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodedMessage}`, '_blank');
  };

  return (
    <div className="fixed bottom-6 right-6 z-[9990]" data-testid="chat-widget">
      {/* Chat Popup */}
      {isOpen && (
        <div 
          className="absolute bottom-20 right-0 w-80 bg-gray-900 rounded-2xl shadow-2xl border border-gray-700 overflow-hidden animate-in slide-in-from-bottom-5 duration-300"
          data-testid="chat-popup"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-orange-500 via-purple-500 to-blue-500 p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <MessageCircle className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h3 className="text-white font-bold text-sm">Chat with us</h3>
                  <p className="text-white/80 text-xs">We typically reply instantly</p>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="w-8 h-8 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-colors"
                data-testid="chat-close-btn"
              >
                <X className="h-4 w-4 text-white" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="p-4">
            {/* Welcome Message */}
            <div className="bg-gray-800 rounded-2xl rounded-tl-sm p-4 mb-4">
              <p className="text-gray-300 text-sm leading-relaxed">
                👋 Hi there! Welcome to TD Capital.
              </p>
              <p className="text-gray-400 text-sm mt-2 leading-relaxed">
                Have questions about trading, accounts, or our platform? Our support team is here to help you 24/7.
              </p>
            </div>

            {/* Online Status */}
            <div className="flex items-center gap-2 mb-4">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-xs text-gray-400">Support team is online</span>
            </div>

            {/* WhatsApp Button */}
            <button
              onClick={handleWhatsAppClick}
              className="w-full bg-[#25D366] hover:bg-[#20bd5a] text-white font-semibold py-3 px-4 rounded-xl flex items-center justify-center gap-2 transition-all transform hover:scale-[1.02] active:scale-[0.98]"
              data-testid="whatsapp-btn"
            >
              <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              Chat on WhatsApp
            </button>

            {/* Alternative Contact */}
            <div className="mt-4 pt-4 border-t border-gray-700">
              <p className="text-xs text-gray-500 text-center">
                Or email us at{' '}
                <a href="mailto:support@tdfxcapital.com" className="text-primary hover:underline">
                  support@tdfxcapital.com
                </a>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`
          group relative w-16 h-16 rounded-full shadow-2xl flex items-center justify-center
          transition-all duration-300 transform hover:scale-110
          ${isOpen 
            ? 'bg-gray-800 rotate-0' 
            : 'bg-gradient-to-r from-orange-500 via-purple-500 to-blue-500 animate-pulse hover:animate-none'
          }
        `}
        data-testid="chat-toggle-btn"
      >
        {/* Ripple Effect */}
        {!isOpen && (
          <>
            <span className="absolute w-full h-full rounded-full bg-gradient-to-r from-orange-500 via-purple-500 to-blue-500 opacity-40 animate-ping"></span>
            <span className="absolute w-20 h-20 rounded-full bg-gradient-to-r from-orange-500/20 via-purple-500/20 to-blue-500/20"></span>
          </>
        )}
        
        {/* Icon */}
        <div className="relative z-10 transition-transform duration-300">
          {isOpen ? (
            <X className="h-7 w-7 text-white" />
          ) : (
            <MessageCircle className="h-7 w-7 text-white" />
          )}
        </div>

        {/* Tooltip */}
        {!isOpen && (
          <span className="absolute right-full mr-3 whitespace-nowrap bg-gray-900 text-white text-sm font-medium px-3 py-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
            Chat with us
            <span className="absolute top-1/2 -right-1 -translate-y-1/2 w-2 h-2 bg-gray-900 rotate-45"></span>
          </span>
        )}
      </button>
    </div>
  );
};

export default ChatWidget;
